import {
	createContext,
	useContext,
	useState,
	useEffect,
	useCallback,
} from "react";
import { useParams } from "react-router-dom";
import { useFetchCourseData } from "@/config/strapiService";

const CourseContext = createContext();

export const useCourse = () => {
	const context = useContext(CourseContext);
	if (!context) {
		throw new Error("useCourse must be used within a CourseProvider");
	}
	return context;
};

export const CourseProvider = ({ children }) => {
	const { courseId } = useParams();
	const [currentCourse, setCurrentCourse] = useState(null);
	const [allLessonsByUnit, setAllLessonsByUnit] = useState({});
	const [flatLessons, setFlatLessons] = useState([]);
	const [allQuizzesByUnit, setAllQuizzesByUnit] = useState({});
	const [flatQuizzes, setFlatQuizzes] = useState([]);
	const [currentLesson, setCurrentLesson] = useState(null);
	const [lessonNavigation, setLessonNavigation] = useState({
		currentIndex: -1,
		nextLesson: null,
		prevLesson: null,
	});

	// Fetch course data from Strapi
	const { courseData, isLoading, error } = useFetchCourseData(courseId);

	// Process course data when it's available
	useEffect(() => {
		if (courseData && !isLoading) {
			setCurrentCourse(courseData);

			// Build lessons and quizzes organized by unit
			const lessonsByUnit = {};
			const quizzesByUnit = {};
			const flatLessonsArray = [];
			const flatQuizzesArray = [];

			courseData.units?.forEach((unit) => {
				const unitLessons =
					unit.lessons?.map((lesson) => ({
						...lesson,
						unitId: unit.id,
						unitDocumentId: unit.documentId,
						unitTitle: unit.title,
						unitSlug: unit.slug,
					})) || [];

				// Store lessons by unit using documentId as key
				lessonsByUnit[unit.documentId] = {
					unitId: unit.id,
					unitDocumentId: unit.documentId,
					unitTitle: unit.title,
					unitSlug: unit.slug,
					lessons: unitLessons,
					lessonCount: unitLessons.length,
				};

				// Process quizzes from lessons. A lesson can now have several
				// real, independently-named Quiz entities (lesson.quizzes) —
				// each becomes its own entry here. A lesson that predates the
				// Quiz content-type and hasn't been migrated yet still only
				// carries the old unnamed quiz_question list (lesson.questions),
				// so that's kept as a fallback, synthesized into a single quiz
				// the same way this always worked.
				const unitQuizzes = [];
				const validQuestionsOf = (questions) =>
					(questions || []).filter(
						(question) =>
							question.title &&
							question.title.trim() !== "" &&
							question.options &&
							question.options.length > 0
					);
				const toQuizQuestions = (questions) =>
					questions.map((question) => ({
						id: question.id,
						question: question.title,
						correctAnswer: question.correctAnswer,
						options: question.options.map((option) => ({
							id: option.id,
							label: option.label,
							text: option.answer,
							isCorrect: option.isCorrect,
							explanation: option.explanation,
						})),
					}));

				unit.lessons?.forEach((lesson) => {
					const realQuizzes = lesson.quizzes || [];

					if (realQuizzes.length > 0) {
						realQuizzes.forEach((rawQuiz) => {
							const validQuestions = validQuestionsOf(
								rawQuiz.questions
							);
							if (validQuestions.length === 0) return;

							const quiz = {
								id: rawQuiz.documentId,
								lessonId: lesson.id,
								lessonDocumentId: lesson.documentId,
								lessonTitle: lesson.title,
								lessonSlug: lesson.slug,
								unitId: unit.id,
								unitDocumentId: unit.documentId,
								unitTitle: unit.title,
								unitSlug: unit.slug,
								title: rawQuiz.title,
								questions: toQuizQuestions(validQuestions),
								questionCount: validQuestions.length,
							};

							unitQuizzes.push(quiz);
							flatQuizzesArray.push(quiz);
						});
						return;
					}

					// Fallback for a lesson that hasn't been migrated to Quiz yet.
					const validQuestions = validQuestionsOf(lesson.questions);
					if (validQuestions.length === 0) return;

					const quiz = {
						id: `lesson-${lesson.documentId}`,
						lessonId: lesson.id,
						lessonDocumentId: lesson.documentId,
						lessonTitle: lesson.title,
						lessonSlug: lesson.slug,
						unitId: unit.id,
						unitDocumentId: unit.documentId,
						unitTitle: unit.title,
						unitSlug: unit.slug,
						title: `${lesson.title} Quiz`,
						questions: toQuizQuestions(validQuestions),
						questionCount: validQuestions.length,
					};

					unitQuizzes.push(quiz);
					flatQuizzesArray.push(quiz);
				});

				// Store quizzes by unit using documentId as key
				if (unitQuizzes.length > 0) {
					quizzesByUnit[unit.documentId] = {
						unitId: unit.id,
						unitDocumentId: unit.documentId,
						unitTitle: unit.title,
						unitSlug: unit.slug,
						quizzes: unitQuizzes,
						quizCount: unitQuizzes.length,
					};
				}

				// Add to flat array for navigation
				flatLessonsArray.push(...unitLessons);
			});

			setAllLessonsByUnit(lessonsByUnit);
			setFlatLessons(flatLessonsArray);
			setAllQuizzesByUnit(quizzesByUnit);
			setFlatQuizzes(flatQuizzesArray);
		}
	}, [courseData, isLoading]);

	// Helper function to set current lesson and calculate navigation
	const setLessonContext = useCallback(
		(lessonId) => {
			if (!lessonId || flatLessons.length === 0) return;

			const currentIndex = flatLessons.findIndex(
				(lesson) =>
					lesson.documentId === lessonId ||
					lesson.id.toString() === lessonId.toString()
			);

			if (currentIndex === -1) {
				// If specific lesson not found, default to first lesson
				setCurrentLesson(flatLessons[0]);
				setLessonNavigation({
					currentIndex: 0,
					nextLesson: flatLessons.length > 1 ? flatLessons[1] : null,
					prevLesson: null,
				});
			} else {
				setCurrentLesson(flatLessons[currentIndex]);
				setLessonNavigation({
					currentIndex,
					nextLesson:
						currentIndex < flatLessons.length - 1
							? flatLessons[currentIndex + 1]
							: null,
					prevLesson:
						currentIndex > 0 ? flatLessons[currentIndex - 1] : null,
				});
			}
		},
		[flatLessons]
	);

	// Helper function to get lesson by documentId or id
	const getLessonById = (lessonId) => {
		return flatLessons.find(
			(lesson) =>
				lesson.documentId === lessonId ||
				lesson.id.toString() === lessonId.toString()
		);
	};

	// Helper function to get lessons by unit documentId
	const getLessonsByUnit = (unitDocumentId) => {
		return allLessonsByUnit[unitDocumentId]?.lessons || [];
	};

	// Helper function to get all lessons for a unit
	const getUnitLessons = (unitDocumentId) => {
		return allLessonsByUnit[unitDocumentId] || null;
	};

	// Helper function to get unit by documentId
	const getUnitById = (unitDocumentId) => {
		return currentCourse?.units?.find(
			(unit) => unit.documentId === unitDocumentId
		);
	};

	// Helper function to get quiz by lesson documentId
	const getQuizByLessonId = (lessonId) => {
		return flatQuizzes.find(
			(quiz) =>
				quiz.lessonDocumentId === lessonId ||
				quiz.lessonId.toString() === lessonId.toString()
		);
	};

	// Helper function to get quizzes by unit documentId
	const getQuizzesByUnit = (unitDocumentId) => {
		return allQuizzesByUnit[unitDocumentId]?.quizzes || [];
	};

	// Helper function to get all quizzes for a unit
	const getUnitQuizzes = (unitDocumentId) => {
		return allQuizzesByUnit[unitDocumentId] || null;
	};

	// Helper function to get all quizzes (flat array)
	const getAllQuizzes = () => {
		return flatQuizzes;
	};

	// Helper function to get lesson position within its unit
	const getLessonPositionInUnit = (lessonId) => {
		for (const unitDocumentId in allLessonsByUnit) {
			const unitData = allLessonsByUnit[unitDocumentId];
			const lessonIndex = unitData.lessons.findIndex(
				(lesson) =>
					lesson.documentId === lessonId ||
					lesson.id.toString() === lessonId.toString()
			);

			if (lessonIndex !== -1) {
				return {
					unitId: unitData.unitId,
					unitDocumentId: unitData.unitDocumentId,
					unitTitle: unitData.unitTitle,
					lessonIndex,
					totalLessonsInUnit: unitData.lessons.length,
					position: `${lessonIndex + 1}/${unitData.lessons.length}`,
				};
			}
		}
		return null;
	};

	// Helper function to check if lesson has quiz
	const lessonHasQuiz = (lessonId) => {
		return flatQuizzes.some(
			(quiz) =>
				quiz.lessonDocumentId === lessonId ||
				quiz.lessonId.toString() === lessonId.toString()
		);
	};

	// Helper function to get course statistics
	const getCourseStats = () => {
		const totalUnits = currentCourse?.units?.length || 0;
		const totalLessons = flatLessons.length;
		const totalQuizzes = flatQuizzes.length;

		// Count lessons with videos
		const lessonsWithVideos = flatLessons.filter(
			(lesson) => lesson.videoUrl
		).length;

		return {
			totalUnits,
			totalLessons,
			totalQuizzes,
			lessonsWithVideos,
			unitsWithQuizzes: Object.keys(allQuizzesByUnit).length,
		};
	};

	const value = {
		// Core data
		courseData: currentCourse,
		isLoading,
		error,
		courseId,

		// Lessons organized by unit
		allLessonsByUnit,
		flatLessons,
		currentLesson,
		lessonNavigation,
		setLessonContext,
		getLessonById,
		getLessonsByUnit,
		getUnitLessons,
		getLessonPositionInUnit,

		// Quizzes organized by unit
		allQuizzesByUnit,
		flatQuizzes,
		getQuizByLessonId,
		getQuizzesByUnit,
		getUnitQuizzes,
		getAllQuizzes,
		lessonHasQuiz,

		// Units
		getUnitById,

		// Statistics
		getCourseStats,

		// Helper functions
		formatDate: (dateString) => {
			if (!dateString) return "";
			return new Date(dateString).toLocaleDateString("en-US", {
				year: "numeric",
				month: "long",
				day: "numeric",
			});
		},

		// YouTube URL helper
		getEmbedUrl: (url) => {
			if (!url) return null;

			const regExp =
				/^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
			const match = url.match(regExp);

			if (match && match[2].length === 11) {
				return `https://www.youtube.com/embed/${match[2]}`;
			}

			if (url.includes("/embed/")) {
				return url;
			}

			return url;
		},
	};

	return (
		<CourseContext.Provider value={value}>
			{children}
		</CourseContext.Provider>
	);
};
